import { useContext, useRef, useState } from 'react';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    propsDataLocationToPathAndValue,
    TemplateContext,
    useEvaluatedAttributes,
} from '@ea-lab/reactive-json';

/**
 * StableTextField — un champ texte dont le CURSEUR survit à un aller-retour du
 * magasin. Même liaison que `TextField` / `TextAreaField` (`dataLocation`), même
 * écriture à chaque frappe : seule la source de la valeur AFFICHÉE change.
 *
 * Pourquoi du JS et pas du YAML : dans un `ReactiveJsonSubroot` en
 * `sharedUpdates`, `updateData` part vers le magasin de la PAGE et n'écrit PAS
 * le magasin local (cf. `ReactiveJsonRoot`, fonction d'aiguillage amont). Le
 * magasin local n'est réaligné qu'un rendu plus tard, par l'effet qui rejoue
 * `dataOverride`. Entre les deux, l'`Input` de reactive-json — entièrement
 * contrôlé — se rend avec l'ANCIENNE valeur : React réécrit `input.value`, le
 * navigateur remet le curseur en fin de champ, et la frappe suivante repart de
 * là. Aucune option de `TextField` n'agit sur ce cycle, d'où ce champ.
 *
 * La règle tient en une phrase : le champ n'adopte la valeur du magasin que
 * lorsqu'elle CHANGE. Le rendu périmé qui faisait sauter le curseur, lui, porte
 * une valeur INCHANGÉE — il est donc ignoré, sans rien masquer des écritures
 * venues d'ailleurs (réordonnancement de la liste, réamorçage du formulaire),
 * qui s'affichent aussitôt même si le champ a le focus. C'est indispensable ici :
 * dans une liste, `~.value` est un chemin POSITIONNEL — après un déplacement, la
 * même ligne pointe un autre item, et un brouillon conservé réécrirait le voisin.
 *
 * Props YAML :
 *   dataLocation      – liaison de formulaire standard
 *   defaultFieldValue – valeur quand le chemin est vide (défaut : "")
 *   inputType         – type HTML de l'`<input>` (défaut : "text")
 *   rows              – présent ⇒ `<textarea>` (et non plus un `<input>`)
 *   placeholder
 *   attributes        – sur l'enveloppe (rendue seulement si non vide)
 *   inputAttributes   – sur le champ (class, id, aria-*…) ; gagne sur
 *                       `placeholder`/`inputType`/`rows`, comme dans l'`Input`
 *                       de reactive-json
 * NON supportées (à la différence de `TextField`) : `label`, `forceWrapper` et
 * la génération automatique d'`id` — poser le libellé en YAML (`label` + `for`)
 * et l'`id` dans `inputAttributes`.
 *
 * Exemple :
 *   - type: StableTextField
 *     dataLocation: ~~.siteForm.data.name
 *     inputAttributes: { class: "input", id: "site-name" }
 */
export const StableTextField = ({ props, datafield, path }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);
    const resolve = (valueToEvaluate) =>
        evaluateTemplateValue({ valueToEvaluate, globalDataContext, templateContext });

    const { formData, formDataPath } = propsDataLocationToPathAndValue({
        currentPath: path,
        datafield,
        dataLocation: props.dataLocation,
        defaultValue: props.defaultFieldValue ?? '',
        globalDataContext,
        templateContext,
    });
    // Coercition : un chemin mal visé peut rendre un objet, que la comparaison
    // ci-dessous jugerait différent à CHAQUE rendu (« Too many re-renders »).
    const stored = formData === undefined || formData === null ? '' : String(formData);

    const attributes = useEvaluatedAttributes(props.attributes ?? {});
    const inputAttributes = useEvaluatedAttributes(props.inputAttributes ?? {});
    const rows = resolve(props.rows);

    // `fieldRef` sert aussi aux actions d'attribut (`attributeTransforms`) :
    // `SetAttributeValue` et consorts écrivent sur ce nœud et ne font RIEN sans
    // lui — silencieusement (cf. `dist/component/action/SetAttributeValue.js`).
    const fieldRef = useRef(null);
    const [focused, setFocused] = useState(false);
    const [draft, setDraft] = useState(stored);
    const seen = useRef(stored);
    // Mise à jour PENDANT le rendu (état dérivé) : React refait le rendu
    // aussitôt, sans passage par le DOM — donc sans clignotement, contrairement
    // à un effet.
    if (seen.current !== stored) {
        seen.current = stored;
        if (stored !== draft) setDraft(stored);
    } else if (!focused && stored !== draft) {
        // Filet : hors saisie, le magasin fait foi de toute façon.
        setDraft(stored);
    }

    const handleChange = (event) => {
        const value = event.currentTarget.value;
        setDraft(value);
        globalDataContext.updateData(value, formDataPath);
    };

    // `placeholder`, `type` et `rows` AVANT l'étalement : le YAML doit pouvoir
    // les poser dans `inputAttributes`, comme avec l'`Input` de reactive-json.
    // `value` et les gestionnaires, eux, viennent après : ce sont eux qui font
    // le champ.
    const fieldProps = {
        placeholder: resolve(props.placeholder) ?? '',
        ...inputAttributes,
        value: draft,
        onChange: handleChange,
        onFocus: () => setFocused(true),
        // Le passage par l'état (et non un ref) force un rendu à la sortie du
        // champ : c'est lui qui rebranche l'affichage sur le magasin.
        onBlur: () => setFocused(false),
        ref: fieldRef,
    };

    const field =
        rows === undefined || rows === null || rows === '' ? (
            <input type={resolve(props.inputType) ?? 'text'} {...fieldProps} />
        ) : (
            <textarea rows={rows} {...fieldProps} />
        );

    return (
        <ActionDependant {...props} attributesHolderRef={fieldRef}>
            {Object.keys(attributes).length > 0 ? <div {...attributes}>{field}</div> : field}
        </ActionDependant>
    );
};
